/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    code: "code",
    h3: "h3",
    a: "a",
    pre: "pre",
    blockquote: "blockquote",
    h4: "h4",
    em: "em"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Server Side Rendering | v2+"
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components supports concurrent server side rendering, with stylesheet rehydration.\nThe basic idea is that everytime you render your app on the server, you can create\na ", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " and add a provider to your React tree, that accepts styles\nvia a context API."]
    }), "\n", _jsxs(_components.p, {
      children: ["This doesn't interfere with global styles, such as ", _jsx(_components.code, {
        children: "keyframes"
      }), " or ", _jsx(_components.code, {
        children: "createGlobalStyle"
      }), " and\nallows you to use styled-components with React DOM's various SSR APIs."]
    }), "\n", _jsx(_components.h3, {
      children: "Tooling setup"
    }), "\n", _jsxs(_components.p, {
      children: ["In order to reliably perform server side rendering and have the client side bundle pick up without issues, you'll need to use our ", _jsx(_components.a, {
        href: "/docs/tooling#babel-plugin",
        children: "babel plugin"
      }), ". It prevents checksum mismatches by adding a deterministic ID to each styled component. Refer to the ", _jsx(_components.a, {
        href: "/docs/tooling#serverside-rendering",
        children: "tooling documentation"
      }), " for more information."]
    }), "\n", _jsxs(_components.p, {
      children: ["For TypeScript users, our resident TS guru Igor Oleinikov put together a ", _jsx(_components.a, {
        href: "/docs/tooling#typescript-plugin",
        children: "TypeScript plugin"
      }), " for the webpack ts-loader / awesome-typescript-loader toolchain that accomplishes some similar tasks."]
    }), "\n", _jsxs(_components.p, {
      children: ["If possible, we definitely recommend using the babel plugin though because it is updated the most frequently. It's now possible to ", _jsx(_components.a, {
        href: "https://babeljs.io/docs/en/babel-preset-typescript",
        children: "compile TypeScript using Babel"
      }), ", so it may be worth switching off TS loader and onto a pure Babel implementation to reap the ecosystem benefits."]
    }), "\n", _jsx(_components.h3, {
      children: "Example"
    }), "\n", _jsx(_components.p, {
      children: "The basic API goes as follows:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToString } from 'react-dom/server';\nimport { ServerStyleSheet } from 'styled-components';\n\nconst sheet = new ServerStyleSheet();\ntry {\n  const html = renderToString(sheet.collectStyles(<YourApp />));\n  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();\n} catch (error) {\n  // handle error\n  console.error(error);\n} finally {\n  sheet.seal();\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "collectStyles"
      }), " method wraps your element in a provider. Optionally you can use\nthe ", _jsx(_components.code, {
        children: "StyleSheetManager"
      }), " provider directly, instead of this method. Just make sure not to\nuse it on the client-side."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToString } from 'react-dom/server';\nimport { ServerStyleSheet, StyleSheetManager } from 'styled-components';\n\nconst sheet = new ServerStyleSheet();\ntry {\n  const html = renderToString(\n    <StyleSheetManager sheet={sheet.instance}>\n      <YourApp />\n    </StyleSheetManager>\n  );\n  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();\n} catch (error) {\n  // handle error\n  console.error(error);\n} finally {\n  sheet.seal();\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " returns a string of multiple ", _jsx(_components.code, {
        children: "<style>"
      }), " tags.\nYou need to take this into account when adding the CSS string to your HTML output."]
    }), "\n", _jsxs(_components.p, {
      children: ["Alternatively the ", _jsx(_components.code, {
        children: "ServerStyleSheet"
      }), " instance also has a ", _jsx(_components.code, {
        children: "getStyleElement()"
      }), " method\nthat returns an array of React elements."]
    }), "\n", _jsxs(_components.p, {
      children: ["If rendering fails for any reason it's a good idea to use ", _jsx(_components.code, {
        children: "try...catch...finally"
      }), " to ensure that the ", _jsx(_components.code, {
        children: "sheet"
      }), " object will always be available for garbage collection. Make sure ", _jsx(_components.code, {
        children: "sheet.seal()"
      }), " is only called after ", _jsx(_components.code, {
        children: "sheet.getStyleTags()"
      }), " or ", _jsx(_components.code, {
        children: "sheet.getStyleElement()"
      }), " have been called otherwise a different error will be thrown."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "sheet.getStyleTags()"
        }), " and ", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " can only be called after your element is rendered. As a result, components from ", _jsx(_components.code, {
          children: "sheet.getStyleElement()"
        }), " cannot be combined with ", _jsx(_components.code, {
          children: "<YourApp />"
        }), " into a larger component."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Next.js"
    }), "\n", _jsx(_components.h4, {
      children: "With Babel"
    }), "\n", _jsxs(_components.p, {
      children: ["Basically you need to add a custom ", _jsx(_components.code, {
        children: "pages/_document.js"
      }), " (if you don't have one). Then ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components-babel/pages/_document.tsx",
        children: "copy the logic"
      }), " for styled-components to inject the server side rendered styles into the ", _jsx(_components.code, {
        children: "<head>"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["Refer to ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components-babel",
        children: "our example"
      }), " in the Next.js repo for an up-to-date usage example."]
    }), "\n", _jsx(_components.h4, {
      children: "With SWC"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://nextjs.org/blog/next-12",
        children: "Since version 12"
      }), ", Next.js uses a Rust compiler called SWC. If you're not using any babel plugin, you should refer to ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components",
        children: "this example"
      }), " instead."]
    }), "\n", _jsxs(_components.p, {
      children: ["On this version, you ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/next.config.js",
        children: "only need to add"
      }), " ", _jsx(_components.code, {
        children: "styledComponents: true,"
      }), " at the compiler options in the ", _jsx(_components.code, {
        children: "next.config.js"
      }), " file and modify ", _jsx(_components.code, {
        children: "_document"
      }), " file with ", _jsx(_components.code, {
        children: "getInitialProps"
      }), " as in this ", _jsx(_components.a, {
        href: "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/pages/_document.tsx",
        children: "example"
      }), " to support SSR."]
    }), "\n", _jsx(_components.h4, {
      children: "App directory"
    }), "\n", _jsxs(_components.p, {
      children: ["For routes defined in the ", _jsx(_components.code, {
        children: "app/"
      }), " directory, in Next.js v13+, you'll need to put a styled-components registry in one of your layout files, as ", _jsx(_components.a, {
        href: "https://nextjs.org/docs/app/building-your-application/styling/css-in-js#styled-components",
        children: "described in Next.js docs"
      }), ". Note that this depends on styled-components v6+. Also note that the ", _jsx(_components.code, {
        children: "'use client'"
      }), " directive is used - so while your page will be server-side rendered, styled-components will still appear in your client bundle."]
    }), "\n", _jsx(_components.h3, {
      children: "Gatsby"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.gatsbyjs.org/",
        children: "Gatsby"
      }), " has an official plugin that enables server-side rendering for styled-components."]
    }), "\n", _jsxs(_components.p, {
      children: ["Refer to ", _jsx(_components.a, {
        href: "https://www.gatsbyjs.org/packages/gatsby-plugin-styled-components/",
        children: "the plugin's page"
      }), " for setup and usage instructions."]
    }), "\n", _jsx(_components.h3, {
      children: "Streaming Rendering"
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components offers a streaming API for use with ", _jsx(_components.a, {
        href: "https://reactjs.org/docs/react-dom-server.html#rendertonodestream",
        children: "ReactDOMServer.renderToNodeStream()"
      }), ". There are two parts to a streaming implementation:"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "On the server:"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "ReactDOMServer.renderToNodeStream"
      }), " emits a \"readable\" stream that styled-components wraps. As whole chunks of HTML are pushed onto the stream, if any corresponding styles are ready to be rendered, a style block is prepended to React's HTML and forwarded on to the client browser."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { renderToNodeStream } from 'react-dom/server';\nimport styled, { ServerStyleSheet } from 'styled-components';\n\n// if you're using express.js, you'd have access to the response object \"res\"\n\n// typically you'd want to write some preliminary HTML, since React doesn't handle this\nres.write('<html><head><title>Test</title></head><body>');\n\nconst Heading = styled.h1`\n  color: red;\n`;\n\nconst sheet = new ServerStyleSheet();\nconst jsx = sheet.collectStyles(<Heading>Hello SSR!</Heading>);\nconst stream = sheet.interleaveWithNodeStream(renderToNodeStream(jsx));\n\n// you'd then pipe the stream into the response object until it's done\nstream.pipe(res, { end: false });\n\n// and finalize the response with closing HTML\nstream.on('end', () => res.end('</body></html>'));\n"
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "On the client:"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { hydrate } from 'react-dom';\n\nhydrate();\n// your client-side react implementation\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "After client-side rehydration is complete, styled-components will take over as usual and inject any further dynamic styles after the relocated streaming ones."
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
